/**
 * Trovva — Stage Gate Modal
 *
 * When a lead is moved to a stage that requires extra info, this modal
 * prompts the broker/agent to fill those fields before the status change persists.
 *
 * Stages handled:
 *   - qualified:        budget + timeline
 *   - offer_submitted:  property address, offer amount, date, contingencies, notes
 *                        → creates first entry in offer_history
 *   - offer_accepted:   accepted price, closing date, buyer/seller attorneys
 *                        → creates 'accepted' entry in offer_history
 *                        → triggers auto-creation of Deal in Transactions
 */
import { useState, useEffect } from 'react';
import { CheckSquare, X, DollarSign, Home, Calendar, FileText, Users } from 'lucide-react';
import { theme } from '../../lib/theme';
import { STAGE_REQUIREMENTS, CONTINGENCY_OPTIONS, missingForStage } from '../../lib/stageGating';

export default function StageGateModal({ lead, targetStatus, onConfirm, onCancel }) {
  const isOffer = targetStatus === 'offer_submitted';
  const isAccepted = targetStatus === 'offer_accepted';
  const isLoi = targetStatus === 'loi_submitted';
  const isLoiAccepted = targetStatus === 'loi_accepted';
  const isCommercial = lead?.lead_type === 'commercial';

  const [values, setValues] = useState({
    // Qualified fields
    budget: lead?.budget || '',
    timeline: lead?.timeline || '',
    preapproved: lead?.preapproved ?? null,
    preapproval_amount: lead?.preapproval_amount || '',
    // Offer fields
    property_address: lead?.property_address || '',
    offer_amount: lead?.offer_amount || '',
    offer_date: lead?.offer_date || new Date().toISOString().split('T')[0],
    contingencies: lead?.contingencies || ['inspection', 'financing', 'attorney_review'],
    offer_notes: lead?.offer_notes || '',
    // Accepted fields
    accepted_price: lead?.accepted_price || lead?.offer_amount || '',
    closing_date_target: lead?.closing_date_target || '',
    buyer_attorney_name: lead?.buyer_attorney_name || '',
    buyer_attorney_phone: lead?.buyer_attorney_phone || '',
    seller_attorney_name: lead?.seller_attorney_name || '',
    seller_attorney_phone: lead?.seller_attorney_phone || '',
    // Commercial LOI fields
    loi_amount: lead?.loi_amount || '',
    commercial_sub_type: lead?.commercial_sub_type || '',
    leasable_sf: lead?.desired_sf || '',
    lease_rate: '',
    lease_term_months: '',
    due_diligence_days: '30',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(v => ({
      ...v,
      budget: lead?.budget || v.budget,
      timeline: lead?.timeline || v.timeline,
      property_address: lead?.property_address || v.property_address,
      offer_amount: lead?.offer_amount || v.offer_amount,
      accepted_price: lead?.accepted_price || lead?.offer_amount || v.accepted_price,
    }));
  }, [lead]);

  const missing = missingForStage({ ...lead, ...values }, targetStatus);
  const canConfirm = missing.length === 0;

  const LABELS = {
    qualified: 'Qualified',
    offer_submitted: 'Offer Submitted',
    offer_accepted: 'Offer Accepted',
    loi_submitted: 'LOI Submitted',
    loi_accepted: 'LOI Accepted',
  };
  const targetLabel = LABELS[targetStatus] || targetStatus;

  const toggleContingency = (key) => {
    setValues(v => {
      const current = v.contingencies || [];
      return {
        ...v,
        contingencies: current.includes(key)
          ? current.filter(c => c !== key)
          : [...current, key],
      };
    });
  };

  const formatCurrency = (raw) => {
    const num = String(raw).replace(/[^0-9]/g, '');
    return num ? Number(num).toLocaleString('en-US') : '';
  };

  const submit = async () => {
    setSaving(true);
    try {
      const patch = { lead_status: targetStatus };

      // Qualified fields
      if (values.budget) patch.budget = values.budget;
      if (values.timeline) patch.timeline = values.timeline;
      if (values.preapproved !== null) patch.preapproved = values.preapproved;
      if (values.preapproval_amount) {
        const num = parseFloat(String(values.preapproval_amount).replace(/[^\d.]/g, ''));
        if (!Number.isNaN(num)) patch.preapproval_amount = num;
      }

      // Offer Submitted fields
      if (isOffer || isAccepted) {
        patch.property_address = values.property_address;
        patch.offer_date = values.offer_date;
        patch.contingencies = values.contingencies;
        patch.offer_notes = values.offer_notes;

        const offerNum = parseFloat(String(values.offer_amount).replace(/[^\d.]/g, ''));
        if (!Number.isNaN(offerNum)) patch.offer_amount = offerNum;
      }

      if (isOffer) {
        // Create initial offer_history entry
        const offerNum = parseFloat(String(values.offer_amount).replace(/[^\d.]/g, ''));
        const existingHistory = Array.isArray(lead?.offer_history) ? lead.offer_history : [];
        patch.offer_history = [
          ...existingHistory,
          {
            type: 'initial',
            amount: offerNum || 0,
            from: (lead?.lead_type === 'seller') ? 'seller' : 'buyer',
            date: values.offer_date,
            notes: values.offer_notes || '',
            contingencies: values.contingencies,
            created_at: new Date().toISOString(),
          },
        ];
      }

      // Offer Accepted fields
      if (isAccepted) {
        const acceptedNum = parseFloat(String(values.accepted_price).replace(/[^\d.]/g, ''));
        if (!Number.isNaN(acceptedNum)) patch.accepted_price = acceptedNum;
        patch.closing_date_target = values.closing_date_target;
        patch.buyer_attorney_name = values.buyer_attorney_name;
        patch.buyer_attorney_phone = values.buyer_attorney_phone;
        patch.seller_attorney_name = values.seller_attorney_name;
        patch.seller_attorney_phone = values.seller_attorney_phone;

        // Add 'accepted' entry to offer_history
        const existingHistory = Array.isArray(lead?.offer_history) ? lead.offer_history : [];
        patch.offer_history = [
          ...existingHistory,
          {
            type: 'accepted',
            amount: acceptedNum || 0,
            from: (lead?.lead_type === 'seller') ? 'buyer' : 'seller',
            date: new Date().toISOString().split('T')[0],
            notes: `Accepted at $${(acceptedNum || 0).toLocaleString()}. Closing target: ${values.closing_date_target || 'TBD'}`,
            created_at: new Date().toISOString(),
          },
        ];

        // Signal to caller that a deal should be auto-created
        patch._create_deal = true;
        patch._deal_data = {
          property_address: values.property_address,
          offer_price: parseFloat(String(values.offer_amount).replace(/[^\d.]/g, '')) || 0,
          sale_price: acceptedNum || 0,
          closing_date: values.closing_date_target,
          offer_date: values.offer_date || lead?.offer_date,
          attorney_name: values.buyer_attorney_name || values.seller_attorney_name,
          attorney_phone: values.buyer_attorney_phone || values.seller_attorney_phone,
        };
      }

      // ── Commercial LOI Submitted ──
      if (isLoi) {
        patch.property_address = values.property_address;
        const loiNum = parseFloat(String(values.loi_amount).replace(/[^\d.]/g, ''));
        if (!Number.isNaN(loiNum)) patch.loi_amount = loiNum;
      }

      // ── Commercial LOI Accepted → auto-create Deal ──
      if (isLoiAccepted) {
        patch.property_address = values.property_address;
        const loiNum = parseFloat(String(values.loi_amount).replace(/[^\d.]/g, ''));
        if (!Number.isNaN(loiNum)) patch.loi_amount = loiNum;
        patch.commercial_sub_type = values.commercial_sub_type;

        // Determine deal_type from commercial_sub_type
        const subType = values.commercial_sub_type || lead?.commercial_sub_type;
        const isLease = subType === 'tenant_rep' || subType === 'landlord_rep';
        const dealType = isLease ? 'commercial_lease' : 'commercial_sale';

        patch._create_deal = true;
        patch._deal_data = {
          property_address: values.property_address,
          deal_type: dealType,
          commercial_sub_type: subType,
          status: 'due_diligence',
          loi_submitted_date: lead?.offer_date || new Date().toISOString().split('T')[0],
          loi_accepted_date: new Date().toISOString().split('T')[0],
          due_diligence_start: new Date().toISOString().split('T')[0],
          // Lease-specific fields
          ...(isLease ? {
            leasable_sf: parseInt(String(values.leasable_sf).replace(/[^\d]/g, ''), 10) || null,
            lease_rate_sf_year: parseFloat(String(values.lease_rate).replace(/[^\d.]/g, '')) || null,
            lease_term_months: parseInt(values.lease_term_months, 10) || null,
          } : {
            offer_price: loiNum || 0,
            sale_price: loiNum || 0,
          }),
        };
      }

      await onConfirm?.(patch);
    } finally { setSaving(false); }
  };

  const required = STAGE_REQUIREMENTS[targetStatus] || [];
  if (!required.length) {
    onConfirm?.({ lead_status: targetStatus });
    return null;
  }

  return (
    <div onClick={onCancel} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
    }}>
      <style>{`
        .sgm-modal input::placeholder,
        .sgm-modal textarea::placeholder,
        .sgm-modal select option[value=""] {
          color: #C0C9D6 !important;
          opacity: 1;
        }
      `}</style>
      <div className="sgm-modal" onClick={e => e.stopPropagation()} style={{
        background: theme.colors.card, borderRadius: 14, padding: 22,
        width: '100%', maxWidth: isOffer || isAccepted ? 520 : 440,
        boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
        maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: theme.colors.text }}>
              {isAccepted ? '🎉 ' : ''}Moving to <strong style={{ color: theme.colors.primary }}>{targetLabel}</strong>
            </h3>
            <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4, lineHeight: 1.4 }}>
              {isOffer && 'Capture the offer details so you can track the negotiation.'}
              {isAccepted && 'Congratulations! Fill in the final terms — a Deal will be created automatically in Transactions.'}
              {isLoi && 'Capture the LOI details for tracking.'}
              {isLoiAccepted && 'LOI accepted! Fill in the terms — a commercial Deal will be created in Transactions → Due Diligence.'}
              {!isOffer && !isAccepted && !isLoi && !isLoiAccepted && 'Before we advance this lead, we need a few details that matter at this stage.'}
            </div>
          </div>
          <button onClick={onCancel} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, padding: 4, fontSize: 16, lineHeight: 1,
          }}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>

          {/* ── Qualified fields ── */}
          {required.includes('budget') && (
            <Row label="Budget" req icon={DollarSign}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: theme.colors.textLight, pointerEvents: 'none' }}>$</span>
                <input value={values.budget}
                  onChange={e => setValues(v => ({ ...v, budget: formatCurrency(e.target.value) }))}
                  inputMode="numeric" placeholder="400,000"
                  style={{ ...field(), paddingLeft: 24 }}
                />
              </div>
            </Row>
          )}
          {required.includes('timeline') && (
            <Row label="Timeline" req icon={Calendar}>
              <select value={values.timeline} onChange={e => setValues(v => ({ ...v, timeline: e.target.value }))} style={field()}>
                <option value="">Select timeline...</option>
                <option value="ASAP">ASAP</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
                <option value="6 months">6 months</option>
                <option value="6-12 months">6–12 months</option>
                <option value="12+ months">12+ months</option>
                <option value="Exploring">Just exploring</option>
              </select>
            </Row>
          )}

          {/* ── Offer Submitted fields ── */}
          {(isOffer || isAccepted) && (
            <>
              <Row label="Property Address" req icon={Home}>
                <input value={values.property_address}
                  onChange={e => setValues(v => ({ ...v, property_address: e.target.value }))}
                  placeholder="123 Main St, Cherry Hill, NJ 08003"
                  style={field()}
                />
              </Row>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <Row label="Offer Amount" req icon={DollarSign}>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: theme.colors.textLight, pointerEvents: 'none' }}>$</span>
                      <input value={typeof values.offer_amount === 'number' ? values.offer_amount.toLocaleString() : formatCurrency(values.offer_amount)}
                        onChange={e => setValues(v => ({ ...v, offer_amount: formatCurrency(e.target.value) }))}
                        inputMode="numeric" placeholder="450,000"
                        style={{ ...field(), paddingLeft: 24 }}
                      />
                    </div>
                  </Row>
                </div>
                <div style={{ flex: 1 }}>
                  <Row label="Offer Date" req icon={Calendar}>
                    <input type="date" value={values.offer_date}
                      onChange={e => setValues(v => ({ ...v, offer_date: e.target.value }))}
                      style={field()}
                    />
                  </Row>
                </div>
              </div>

              <Row label="Contingencies" icon={FileText}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CONTINGENCY_OPTIONS.map(c => {
                    const active = (values.contingencies || []).includes(c.key);
                    return (
                      <button key={c.key} onClick={() => toggleContingency(c.key)} style={{
                        padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        border: `1.5px solid ${active ? theme.colors.primary : theme.colors.border}`,
                        background: active ? `${theme.colors.primary}10` : 'transparent',
                        color: active ? theme.colors.primary : theme.colors.textLight,
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}>
                        {active ? '✓ ' : ''}{c.label}
                      </button>
                    );
                  })}
                </div>
              </Row>

              {isOffer && (
                <Row label="Notes">
                  <textarea value={values.offer_notes}
                    onChange={e => setValues(v => ({ ...v, offer_notes: e.target.value }))}
                    placeholder="Full price offer with 10-day inspection contingency..."
                    rows={2}
                    style={{ ...field(), resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </Row>
              )}
            </>
          )}

          {/* ── Offer Accepted fields ── */}
          {isAccepted && (
            <>
              <div style={{
                height: 1, background: theme.colors.border, margin: '4px 0',
              }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Final Terms
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <Row label="Accepted Price" req icon={DollarSign}>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: theme.colors.textLight, pointerEvents: 'none' }}>$</span>
                      <input value={typeof values.accepted_price === 'number' ? values.accepted_price.toLocaleString() : formatCurrency(values.accepted_price)}
                        onChange={e => setValues(v => ({ ...v, accepted_price: formatCurrency(e.target.value) }))}
                        inputMode="numeric" placeholder="460,000"
                        style={{ ...field(), paddingLeft: 24 }}
                      />
                    </div>
                  </Row>
                </div>
                <div style={{ flex: 1 }}>
                  <Row label="Target Closing Date" req icon={Calendar}>
                    <input type="date" value={values.closing_date_target}
                      onChange={e => setValues(v => ({ ...v, closing_date_target: e.target.value }))}
                      style={field()}
                    />
                  </Row>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>
                Attorneys
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <Row label="Buyer's Attorney" icon={Users}>
                    <input value={values.buyer_attorney_name}
                      onChange={e => setValues(v => ({ ...v, buyer_attorney_name: e.target.value }))}
                      placeholder="Name" style={field()}
                    />
                    <input value={values.buyer_attorney_phone}
                      onChange={e => setValues(v => ({ ...v, buyer_attorney_phone: e.target.value }))}
                      placeholder="Phone" style={{ ...field(), marginTop: 6 }}
                    />
                  </Row>
                </div>
                <div style={{ flex: 1 }}>
                  <Row label="Seller's Attorney" icon={Users}>
                    <input value={values.seller_attorney_name}
                      onChange={e => setValues(v => ({ ...v, seller_attorney_name: e.target.value }))}
                      placeholder="Name" style={field()}
                    />
                    <input value={values.seller_attorney_phone}
                      onChange={e => setValues(v => ({ ...v, seller_attorney_phone: e.target.value }))}
                      placeholder="Phone" style={{ ...field(), marginTop: 6 }}
                    />
                  </Row>
                </div>
              </div>
            </>
          )}

          {/* ── Commercial LOI fields ── */}
          {(isLoi || isLoiAccepted) && (
            <>
              <Row label="Property Address" req icon={Home}>
                <input value={values.property_address}
                  onChange={e => setValues(v => ({ ...v, property_address: e.target.value }))}
                  placeholder="123 Main St, Suite 200, Newark NJ"
                  style={field()}
                />
              </Row>
              <Row label={isLoiAccepted ? 'Accepted LOI Amount' : 'LOI Amount'} req icon={DollarSign}>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: theme.colors.textLight, pointerEvents: 'none' }}>$</span>
                  <input value={values.loi_amount}
                    onChange={e => setValues(v => ({ ...v, loi_amount: formatCurrency(e.target.value) }))}
                    inputMode="numeric" placeholder="1,200,000 or 28/SF/yr"
                    style={{ ...field(), paddingLeft: 24 }}
                  />
                </div>
              </Row>
            </>
          )}
          {isLoiAccepted && (
            <>
              {!values.commercial_sub_type && (
                <Row label="Deal Type" req icon={FileText}>
                  <select value={values.commercial_sub_type} onChange={e => setValues(v => ({ ...v, commercial_sub_type: e.target.value }))}
                    style={field()}>
                    <option value="">Select...</option>
                    <option value="buyer_rep">Buying Property (Sale)</option>
                    <option value="seller_rep">Selling Property (Sale)</option>
                    <option value="tenant_rep">Looking for Space (Lease)</option>
                    <option value="landlord_rep">Leasing Out Space (Lease)</option>
                  </select>
                </Row>
              )}
              {(values.commercial_sub_type === 'tenant_rep' || values.commercial_sub_type === 'landlord_rep') && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Row label="Leasable SF" icon={Home}>
                      <input value={values.leasable_sf}
                        onChange={e => setValues(v => ({ ...v, leasable_sf: e.target.value.replace(/[^\d]/g, '') }))}
                        inputMode="numeric" placeholder="3,000"
                        style={field()}
                      />
                    </Row>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Row label="Lease Rate ($/SF/yr)" icon={DollarSign}>
                      <input value={values.lease_rate}
                        onChange={e => setValues(v => ({ ...v, lease_rate: e.target.value }))}
                        placeholder="28.00"
                        style={field()}
                      />
                    </Row>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Row label="Term (months)" icon={Calendar}>
                      <input value={values.lease_term_months}
                        onChange={e => setValues(v => ({ ...v, lease_term_months: e.target.value.replace(/[^\d]/g, '') }))}
                        inputMode="numeric" placeholder="60"
                        style={field()}
                      />
                    </Row>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {missing.length > 0 && (
          <div style={{ marginTop: 14, padding: 10, background: '#FEF3C7', borderRadius: 8, fontSize: 11, color: '#92400E' }}>
            Still needed: <strong>{missing.join(', ').replace(/_/g, ' ')}</strong>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} style={{
            padding: '9px 14px', borderRadius: 10,
            background: 'transparent', border: `1px solid ${theme.colors.border}`,
            color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={submit} disabled={!canConfirm || saving} style={{
            padding: '9px 16px', borderRadius: 10, border: 'none',
            background: (isAccepted || isLoiAccepted) ? theme.colors.success : theme.colors.primary,
            color: 'white', fontSize: 12, fontWeight: 700,
            cursor: (!canConfirm || saving) ? 'not-allowed' : 'pointer',
            opacity: (!canConfirm || saving) ? 0.5 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <CheckSquare size={13} />
            {saving ? 'Saving…' : isAccepted ? 'Accept & Create Deal' : isLoiAccepted ? 'Accept LOI & Create Deal' : `Advance to ${targetLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, req, icon: Icon, children }) {
  return (
    <div>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 4,
        fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5,
      }}>
        {Icon && <Icon size={11} />}
        {label}{req && <span style={{ color: theme.colors.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function field() {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 11px', borderRadius: 8,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 13, color: theme.colors.text, background: theme.colors.card,
  };
}
